import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import _ from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import api from '@/api/_legacy'
import {
  ApiTradingAccount,
  ApiTradingAccountApiPaginationResult
} from '@/api/_legacy/generated-api'
import { defaultQueryParams, QueryParams } from '@/api/_legacy/types'
import { TablePaginationParams } from '@/components/core-v2/molecules/table-pagination'
import { formatAccountStatusToDisplayName } from '@/types/tradeApproval'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { TENANT_ID } from '../../tradebuilder-v2/constants'
import { unpascal } from '../../tradebuilder-v2/util'
import { TradingAccount } from '../types'

export function mapToTradingAccount(a: ApiTradingAccount): TradingAccount {
  const output: TradingAccount = {
    accountId: a.accountId,
    accountName: a.accountName,
    noteName: a.note?.noteName,
    status: formatAccountStatusToDisplayName(a.status),
    investment: a.pendingAccountState.deposit,
    isSwitch: a.pendingAccountState.switchRequest !== undefined,
    portfolio: unpascal(a.pendingAccountState.portfolio?.type),
    lastModified: a.pendingAccountState?.lastModified,
    newAccount: a.newAccount
  }

  return output
}

export type TradeSearchFilter = {
  searchTerm: string
  showLiveTrades: boolean
  showNewTrades: boolean
}

export type DraftTradesState = {
  accounts: TradingAccount[]
  currentPageNumber: number
  errors?: any
  filter: TradeSearchFilter
  isLoading: boolean
  numTotalTrades: number
  query: QueryParams
  selectedAccountIdToDelete?: string
}

const initialState: DraftTradesState = {
  numTotalTrades: 0,
  currentPageNumber: 1,
  accounts: [],
  query: { ...defaultQueryParams, PageSize: 20 },
  filter: { searchTerm: '', showLiveTrades: true, showNewTrades: true },
  isLoading: false
}

export interface TradeStatusUpdate {
  accountId: string
  updatedStatus: string
}

export const draftTradesSlice = createSlice({
  name: 'draftTrades',
  initialState,
  reducers: {
    init: state => {
      state.isLoading = true
    },
    deleteDraftTrade: (state, action: PayloadAction<string>) => {
      state.selectedAccountIdToDelete = action.payload
    },
    fetchDraftTradesFailure: (state, action: PayloadAction<any>) => {
      state.isLoading = false
      state.errors = action.payload
    },
    fetchDraftTradesSuccess: (
      state,
      action: PayloadAction<ApiTradingAccountApiPaginationResult>
    ) => {
      const rawAccounts = action.payload.data
      const accounts = rawAccounts.map(mapToTradingAccount)
      const sortedAccounts = _.orderBy(accounts, 'lastModified', 'desc')
      state.accounts = sortedAccounts
      state.numTotalTrades = action.payload.totalDocuments
      state.isLoading = false
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    refreshTrades: () => {},
    filter: (state, action: PayloadAction<string[]>) => {
      state.query.Status = action.payload.join(',')
    },
    setServerFilter: (state, action: PayloadAction<TradeSearchFilter>) => {
      state.isLoading = true
      state.accounts = []
      // reset to first page on any filter change
      state.query.PageNumber = 1

      const filter = action.payload
      if (filter.searchTerm) {
        state.query.WildcardString = filter.searchTerm
      } else {
        state.query.WildcardString = undefined
      }

      if (!filter.showLiveTrades && !filter.showNewTrades) {
        // not allowed to unselect both
        if (!state.filter.showLiveTrades) {
          filter.showLiveTrades = true
        }
        if (!state.filter.showNewTrades) {
          filter.showNewTrades = true
        }
      }
      if (!filter.showLiveTrades && filter.showNewTrades) {
        state.query.FilterNewAccounts = true
      } else if (!filter.showNewTrades && filter.showLiveTrades) {
        state.query.FilterNewAccounts = false
      } else {
        state.query.FilterNewAccounts = undefined
      }

      state.filter = filter
    },
    setPage: (state, action: PayloadAction<TablePaginationParams>) => {
      state.query.PageNumber = action.payload.pageNumber
      state.query.PageSize = action.payload.pageSize
      state.isLoading = true
    },
    clearFilters: state => {
      state.query.Status = ''
      state.query.WildcardString = ''
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const { actions } = draftTradesSlice
export type DraftTradesActions = typeof actions

function* fetchDraftTradesSaga() {
  const rootState = yield select()
  const state = rootState.draftTrades
  const { query } = state

  try {
    const response = yield call(api.tenant.inprogressDetail, TENANT_ID, query)
    yield put({
      type: 'draftTrades/fetchDraftTradesSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'draftTrades/fetchDraftTradesFailure', payload: error })
  }
}

function* deleteDraftTradeSaga() {
  const rootState: RootState = yield select()
  const state = rootState.draftTrades
  const { selectedAccountIdToDelete } = state
  if (!selectedAccountIdToDelete) {
    throw new Error('FATAL ERROR: accountId has not been set')
  }

  try {
    yield call(api.tenant.deleterevertdraftCreate, selectedAccountIdToDelete, TENANT_ID)

    yield put({ type: 'draftTrades/refreshTrades' })
  } catch (error) {
    yield put({ type: 'draftTrades/fetchDraftTradesFailure', payload: error })
  }
}

export function* draftTradesRootSaga() {
  yield all([
    takeLatest('draftTrades/init', fetchDraftTradesSaga),
    takeLatest('draftTrades/search', fetchDraftTradesSaga),
    takeLatest('draftTrades/setServerFilter', fetchDraftTradesSaga),
    takeLatest('draftTrades/setPage', fetchDraftTradesSaga),
    takeLatest('draftTrades/refreshTrades', fetchDraftTradesSaga),
    takeLatest('draftTrades/deleteDraftTrade', deleteDraftTradeSaga)
  ])
}

export default draftTradesSlice.reducer
